/*
PURPOSE: Centralized location for auth-related utilities, cache management, and shared types
USE CASES:
- Retrieving Cognito tokens for authenticated API calls
- Managing shared user profile cache across hooks and route loaders
- Generating random strings for unique identifiers
DOMAIN BOUNDARIES:
- Responsible for low-level auth utilities and cache management only
- Does not contain business logic or authentication flows
- Interacts with AWS Amplify Auth and API layer
- Cache is shared globally via window object for performance
ASSUMPTIONS:
- AWS Amplify Auth is properly configured
- getUserFromAPI is available from api module
- Browser environment (uses window object for caching)
- User profile data structure matches shared/types/platformTypes
*/

import { fetchAuthSession, JWT } from '@aws-amplify/auth';
import { getUserFromAPI } from '../api/api';
import { User } from 'shared/types/platformTypes';
import logger from './logger';

const DEBUG_AUTH = false; // Set to true to enable debug logging

/**
 * Public routes that don't require authentication
 * Shared between App.tsx and routerConfig.tsx
 */
export const PUBLIC_ROUTES = {
  exact: ['/', '/landing', '/about', '/contact-us', '/privacy', '/terms', '/artist-terms', '/for-artists', '/welcome', '/sign-out', '/login', '/trust', '/thankyou', '/principles', '/manifesto', '/notifications/unsubscribe', '/legal', '/legal/common-terms', '/tickets/host-terms', '/tickets/terms', '/tickets/refund-policy'],
  prefixes: ['/c/', '/a/']
};

/**
 * Check if a route is public (doesn't require authentication)
 * @param route - The route path to check
 * @returns true if the route is public
 */
export function isPublicRoute(route: string): boolean {
  if (PUBLIC_ROUTES.exact.includes(route)) {
    return true;
  }
  if (PUBLIC_ROUTES.prefixes.some(prefix => route.startsWith(prefix))) {
    return true;
  }
  return false;
}

/**
 * Cognito tokens interface
 */
interface CognitoTokens {
    idToken: JWT;
    accessToken: JWT;
}

/**
 * Get Cognito tokens from the current session
 * Used for authenticated API calls
 */
export async function getCognitoTokens(): Promise<CognitoTokens | undefined> {
    try {
        const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
        if (!(accessToken && idToken)) {
            throw new Error('Could not retrieve accessToken or idToken');
        }

        return {
            idToken: idToken,
            accessToken: accessToken,
        };
    } catch (err) {
        logger.error('Could not retrieve tokens', err);
        return undefined;
    }
}

/**
 * Generate a random string of specified length
 * Useful for generating unique identifiers
 */
export function generateRandomString(length: number): string {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
}

/**
 * Shared cache interface for user profile data
 */
export interface UserProfileCache {
  userId: string;
  profile: User;
  timestamp: number;
}

/**
 * Extend Window interface for global cache
 */
declare global {
  interface Window {
    sharedUserProfileCache?: UserProfileCache | null;
  }
}

/**
 * Cache duration in milliseconds (5 minutes)
 */
export const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Initialize cache if not exists
 */
if (typeof window !== 'undefined' && !window.sharedUserProfileCache) {
  window.sharedUserProfileCache = null;
}

/**
 * Get cached user profile or fetch from API
 * This is the single source of truth for user profile data fetching
 *
 * @param userId - The user ID to fetch profile for
 * @param force - Force refresh from API (bypass cache)
 * @returns User profile data
 */
export async function getCachedUserProfile(userId: string, force = false): Promise<User> {
  const now = Date.now();
  const cache = window.sharedUserProfileCache;

  // Check cache first (unless forced refresh)
  if (!force && cache &&
      cache.userId === userId &&
      (now - cache.timestamp) < CACHE_DURATION) {
    if (DEBUG_AUTH) console.log('Using cached user profile');
    return cache.profile;
  }

  // Fetch fresh data and update cache
  if (DEBUG_AUTH) console.log('Fetching fresh user profile from API');
  const profile = await getUserFromAPI(userId);

  window.sharedUserProfileCache = {
    userId,
    profile,
    timestamp: now
  };

  return profile;
}

/**
 * Clear the user profile cache
 * Should be called on logout
 */
export function clearUserProfileCache(): void {
  window.sharedUserProfileCache = null;
  if (DEBUG_AUTH) console.log('User profile cache cleared');
}

/**
 * Session storage key for login redirect URL
 */
const LOGIN_REDIRECT_KEY = 'loginRedirectUrl';

/**
 * Store the intended destination URL for post-login redirect
 * Used when an unauthenticated user tries to access a protected route
 *
 * @param url - The full URL path (pathname + search) to redirect to after login
 */
export function setLoginRedirectUrl(url: string): void {
  // Don't store redirects to the home page or public routes
  if (url === '/' || url === '/dashboard' || url === '/login' || isPublicRoute(url)) {
    return;
  }
  sessionStorage.setItem(LOGIN_REDIRECT_KEY, url);
  if (DEBUG_AUTH) console.log('Stored login redirect URL:', url);
}

/**
 * Get and clear the stored login redirect URL
 * Returns the URL and removes it from storage (one-time use)
 *
 * @returns The stored redirect URL, or null if none exists
 */
export function getAndClearLoginRedirectUrl(): string | null {
  const url = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
  if (url) {
    sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    if (DEBUG_AUTH) console.log('Retrieved and cleared login redirect URL:', url);
  }
  return url;
}

/**
 * Clear the login redirect URL without returning it
 * Used when redirect is no longer needed (e.g., manual navigation to home)
 */
export function clearLoginRedirectUrl(): void {
  sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
  if (DEBUG_AUTH) console.log('Cleared login redirect URL');
}